import { Image } from 'expo-image';
import { Text, YStack } from 'tamagui';

import PSLinkButton from '@/components/ui/PSLinkButton';

/**
 * PSFetchError — what a page whose fetch failed draws instead of its content
 * (molecule): the cat, what went wrong, and the way out.
 *
 * Takes the MESSAGE, not the error kind — unlike [[PSHomeSectionError]], which
 * maps kind → copy itself because home draws four of them on one page and would
 * otherwise repeat that lookup four times. A single-list page has one, and
 * `PSErrorClass.message` is already the right sentence per kind (the server's own
 * words on a 4xx, connection copy on a network drop), so a second mapping here
 * would only be a way to disagree with it.
 *
 * ── The cat shows for EVERY kind ──
 * On home it's gated to network failures, on the grounds that the others aren't
 * the reader's to fix — a gate that exists to ration the joke across four
 * sections. There's one of these per page, so it's ungated: a bare sentence under
 * a heading looked like a rendering bug.
 *
 * [[PSHomeSectionError]] stays separate: it carries the home-specific bits (a
 * carousel card's min-height, `withArt`, a dark-surface variant) that this has no
 * use for.
 */

// The no-internet cat, sized as PSHomeSectionError sizes it — same asset, same
// width, so the failure reads identically whichever page the reader hit it on.
// Height follows the source's own 1408×768 ratio, so the art is never stretched:
// change the width and the height keeps up. 300 clears the page gutter
// (PageScrollView's `px="$lg"`) on the narrowest phone we support.
const ART_WIDTH = 300;
const ART_RATIO = 1408 / 768;

export default function PSFetchError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <YStack items="center" gap="$space.lg">
      <Image
        source={require('@/assets/images/no-internet-cat.png')}
        style={{
          width: ART_WIDTH,
          height: ART_WIDTH / ART_RATIO,
          alignSelf: 'center',
        }}
        contentFit="contain"
        transition={200}
      />
      {/* Copy and the link stack tighter ($sm) than they sit under the art ($lg):
          the sentence and its "Retry" are one thought, the art is a separate
          beat. Same treatment as PSHomeSectionError. */}
      <YStack items="center" gap="$space.sm">
        <Text
          fontFamily="$body"
          fontSize="$base"
          color="$textDark"
          textAlign="center"
        >
          {message}
        </Text>
        <PSLinkButton title="Retry" onPress={onRetry} />
      </YStack>
    </YStack>
  );
}
